import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import classNames from "classnames";
import * as React from "react";

import styles from "./Tooltip.module.css";

type BaseUIClassName<State> =
  | string
  | ((state: State) => string | undefined)
  | undefined;

/**
 * Merges a house class with a consumer's. Base UI's `className` is either a
 * string or a function of the part's state, so both forms have to survive.
 */
function withHouseClass<State>(
  houseClass: string,
  className: BaseUIClassName<State>,
): BaseUIClassName<State> {
  return typeof className === "function"
    ? (state: State) => classNames(houseClass, className(state))
    : classNames(houseClass, className);
}

export interface TooltipRootProps extends BaseTooltip.Root.Props {}

function TooltipRoot(props: TooltipRootProps) {
  return <BaseTooltip.Root {...props} />;
}

export interface TooltipTriggerProps extends BaseTooltip.Trigger.Props {}

/**
 * The control the tooltip describes. Timing is per-trigger, not per-tooltip:
 * one tooltip can have several triggers, each with its own `delay`.
 *
 * Give it an `aria-label` unless it already has a visible text label, or the
 * element passed to `render` carries its own. The trigger's name is all
 * assistive technology gets — Base UI deliberately emits no `role="tooltip"`
 * and no `aria-describedby` — so anything a sighted user reads in the tooltip
 * has to be in that name too.
 */
const Trigger = React.forwardRef<HTMLButtonElement, TooltipTriggerProps>(
  function TooltipTriggerPart(props, ref) {
    return <BaseTooltip.Trigger {...props} ref={ref} />;
  },
);

export interface TooltipContentProps
  extends BaseTooltip.Popup.Props,
    Pick<BaseTooltip.Positioner.Props, "side" | "align"> {}

/**
 * The floating surface, fusing Base UI's Portal, Positioner, and Popup.
 *
 * Cap the tooltip's width via `className` and the `--tooltip-max-width`
 * custom property.
 */
const Content = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  function TooltipContentPart(
    { side = "top", align = "center", className, children, ...popupProps },
    ref,
  ) {
    return (
      <BaseTooltip.Portal>
        {/* 8 = --spacing-xs; JS props can't read CSS variables. */}
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={8}
          className={styles.positioner}
        >
          <BaseTooltip.Popup
            {...popupProps}
            ref={ref}
            className={withHouseClass(styles.popup, className)}
          >
            <BaseTooltip.Arrow className={styles.arrow}>
              <ArrowSvg />
            </BaseTooltip.Arrow>
            {children}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    );
  },
);

/**
 * The tail, drawn as a path rather than a CSS shape. It reads the same surface
 * variable as the popup, so retinting the tooltip moves the arrow with it.
 */
function ArrowSvg() {
  return (
    <svg
      className={styles.arrowSvg}
      width="20"
      height="10"
      viewBox="0 0 20 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        className={styles.arrowFill}
        d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
      />
      <path
        className={styles.arrowEdge}
        d="M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z"
      />
    </svg>
  );
}

/**
 * A short, non-interactive label that appears on hover or keyboard focus
 *
 * The popup never takes focus, so it can only ever describe its trigger.
 * Anything the user has to read at leisure or act in belongs in a `Popover`.
 *
 * Label the trigger, not the tooltip. This component treats a tooltip as a visual
 * affordance for sighted pointer and keyboard users — it emits no
 * `role="tooltip"` and no `aria-describedby`, because tooltips reach neither
 * screen readers nor touch users. So give the trigger an accessible name that
 * closely matches the tooltip text, and never let a tooltip be the only place
 * some piece of information appears.
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <Tooltip.Trigger
 *     aria-label="Pin this thread"
 *     render={<RobotoButton variant="secondary" icon={<Pin />} />}
 *   />
 *   <Tooltip.Content>Pin this thread</Tooltip.Content>
 * </Tooltip>
 * ```
 */
export const Tooltip = Object.assign(TooltipRoot, {
  Trigger,
  Content,
});
